import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import styleMatcher, { StyleMatcherDress } from '../services/styleMatcher';
import StyleMatcherCard from '../components/StyleMatcherCard';
import useDeviceType from '../hooks/useDeviceType';

const StyleMatcher: React.FC = () => {
  const [dresses, setDresses] = useState<StyleMatcherDress[]>([]);
  const [likedDresses, setLikedDresses] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allDressesLoaded, setAllDressesLoaded] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const deviceType = useDeviceType();
  const containerRef = useRef<HTMLDivElement>(null);

  // Load dresses
  useEffect(() => {
    loadDresses();
  }, []);

  const loadDresses = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedDresses = await styleMatcher.getStyleMatcherDresses();
      
      if (fetchedDresses.length === 0) {
        setAllDressesLoaded(true);
      } else {
        setDresses(fetchedDresses);
      }
    } catch (err) {
      console.error('Failed to load dresses:', err);
      setError('Failed to load dresses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = useCallback(async (dressId: string) => {
    try {
      // Optimistic update
      setLikedDresses(prev => new Set(prev).add(dressId));
      
      // Record the like
      await styleMatcher.recordSwipe(dressId, 'like');
    } catch (err) {
      console.error('Failed to record like:', err);
      // Revert optimistic update
      setLikedDresses(prev => {
        const newSet = new Set(prev);
        newSet.delete(dressId);
        return newSet;
      });
    }
  }, []);

  const handleEndSession = () => {
    setSessionComplete(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-md mx-auto text-center py-12">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={loadDresses} className="btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (sessionComplete) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-md mx-auto text-center py-12">
          <CheckCircleIcon className="h-16 w-16 text-primary-500 mx-auto mb-4" />
          <h2 className="text-2xl font-serif text-gray-900 mb-2">Session Complete!</h2>
          <p className="text-gray-600 mb-6">
            You liked {likedDresses.size} {likedDresses.size === 1 ? 'dress' : 'dresses'}.
          </p>
          <Link to="/home/style-matcher/results" className="btn-primary inline-block">
            View Results
          </Link>
        </div>
      </div>
    );
  }

  if (allDressesLoaded || dresses.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-md mx-auto text-center py-12">
          <h2 className="text-2xl font-serif text-gray-900 mb-4">No More Dresses</h2>
          <p className="text-gray-600 mb-6">
            {likedDresses.size > 0 
              ? `You've seen all available dresses and liked ${likedDresses.size}.`
              : "You've seen all available dresses."}
          </p>
          {likedDresses.size > 0 && (
            <Link to="/home/style-matcher/results" className="btn-primary inline-block">
              View Results
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center text-gray-600 hover:text-gray-900">
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            <span>Back</span>
          </Link>
          <h1 className="text-xl font-serif text-gray-900">Style Matcher</h1>
          <button 
            onClick={handleEndSession}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            End Session
          </button>
        </div>
      </header>

      {/* Instructions */}
      <div className="px-4 py-3 bg-primary-50 text-center">
        <p className="text-sm text-gray-700">
          {deviceType === 'mobile' 
            ? 'Double tap to like • Scroll down for more'
            : 'Double click to like • Scroll right for more'}
        </p>
      </div>

      {/* Dress Grid/Scroll Container */}
      <div ref={containerRef} className="relative">
        {deviceType === 'mobile' ? (
          // Mobile: Vertical scroll
          <div className="px-4 py-4 space-y-6 max-w-md mx-auto">
            {dresses.map((dress) => (
              <StyleMatcherCard
                key={dress.id}
                dress={dress}
                onLike={handleLike}
                isLiked={likedDresses.has(dress.id)}
              />
            ))}
            
            {/* End of list indicator */}
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">You've reached the end!</p>
              <button 
                onClick={handleEndSession}
                className="btn-primary"
              >
                View Results
              </button>
            </div>
          </div>
        ) : (
          // Desktop: Horizontal scroll
          <div className="horizontal-scroll p-4 gap-4" style={{ height: 'calc(100vh - 140px)' }}>
            {dresses.map((dress) => (
              <div key={dress.id} className="flex-none w-96 snap-center">
                <StyleMatcherCard
                  dress={dress}
                  onLike={handleLike}
                  isLiked={likedDresses.has(dress.id)}
                />
              </div>
            ))}
            
            {/* End of list indicator */}
            <div className="flex-none w-96 snap-center flex items-center justify-center">
              <div className="text-center">
                <p className="text-gray-500 mb-4">You've reached the end!</p>
                <button 
                  onClick={handleEndSession}
                  className="btn-primary"
                >
                  View Results
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StyleMatcher;